/* eslint-disable @typescript-eslint/no-explicit-any */
import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFetchBaseQuery } from "@/redux/apiFetchBaseQuery";
import { PaginationParams } from "@/types/Pagination";
import {
	AiAudioAdResponse,
	AiAudioCreateOrUpdateResponse,
	AiAudioGenerateScriptResponse,
	AiAudioListResponse,
	AiAudioMetaResponse,
	AiAudioPreviewResponse,
} from "@/types/AI/Audio";

export const audioAICreativeSlice = createApi({
	reducerPath: "audioAICreativeSlice",
	baseQuery: apiFetchBaseQuery,
	tagTypes: ["audioAI"],
	endpoints: builder => ({
		getAIMeta: builder.query<AiAudioMetaResponse, void>({
			query: () => ({
				url: `/ai/audio/meta`,
			}),
			providesTags: ["audioAI"],
		}),

		generateScript: builder.mutation<AiAudioGenerateScriptResponse, any>({
			query: formData => ({
				url: "/ai/audio/generate/script",
				method: "POST",
				body: formData,
				headers: {
					accept: "application/json",
				},
			}),
		}),

		generateAudioPreview: builder.mutation<AiAudioPreviewResponse, any>({
			query: formData => ({
				url: "/ai/audio/generate/preview-audio",
				method: "POST",
				body: formData,
				headers: {
					accept: "application/json",
				},
			}),
		}),

		createAudioAdd: builder.mutation<AiAudioCreateOrUpdateResponse, { data: any }>({
			query: ({ data }) => ({
				url: "/ai/audio/build",
				method: "POST",
				body: data,
				headers: {
					accept: "application/json",
				},
			}),
		}),

		updateAudioAdd: builder.mutation<AiAudioCreateOrUpdateResponse, { id: number; data: any }>({
			query: ({ id, data }) => {
				return {
					url: `/ai/audio/build/${id}`,
					method: "PUT",
					body: data,
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
				};
			},
		}),

		getAiAudioList: builder.query<AiAudioListResponse, PaginationParams>({
			query: (queryParams: PaginationParams) => {
				// Build query string from parameters
				const searchParams = new URLSearchParams({
					page_no: queryParams.pageNo.toString(),
					limit: queryParams.pageSize.toString(),
				});

				return {
					url: `/ai/audio/list?${searchParams.toString()}`,
				};
			},
			providesTags: ["audioAI"],
			keepUnusedDataFor: 0, // cache time in seconds
		}),

		getAudioById: builder.query<AiAudioAdResponse, { id: number }>({
			query: ({ id }) => ({
				url: `/ai/audio/${id}`,
			}),
			providesTags: ["audioAI"],
		}),

		downloadAudioAd: builder.query<Blob, { id: number }>({
			query: ({ id }) => ({
				url: `/ai/audio/download/${id}`,
				responseHandler: response => response.blob(),
				cache: "no-cache",
			}),
			providesTags: ["audioAI"],
		}),
	}),
});

export const {
	useGetAIMetaQuery,
	useGenerateScriptMutation,
	useGenerateAudioPreviewMutation,
	useCreateAudioAddMutation,
	useGetAiAudioListQuery,
	useGetAudioByIdQuery,
	useUpdateAudioAddMutation,
	useDownloadAudioAdQuery,
	useLazyDownloadAudioAdQuery,
} = audioAICreativeSlice;
